import React from 'react';
import {
    Text, Container, Divider, Grid, Button, Box
} from 'components/ui';
import Banner from 'components/Content/Banner';
import Copy from 'components/Content/Copy';
import Action from 'components/Content/Action';
import languageLocaleUtils from 'utils/LanguageLocale';
import rwdAdvocacyPageBindings from 'analytics/bindingMethods/pages/rwdAdvocacy/rwdAdvocacyPageBindings';
import { wrapFunctionalComponent } from 'utils/framework';
import { ERROR } from 'components/Campaigns/Referrer/constants';
import AdvocacyErrorEnhancedEN from 'components/Campaigns/RwdAdvocacy/AdvocacyViews/AdvocacyErrorEnhanced/locales/AdvocacyErrorEnhanced_en_US';
import AdvocacyErrorEnhancedFR from 'components/Campaigns/RwdAdvocacy/AdvocacyViews/AdvocacyErrorEnhanced/locales/AdvocacyErrorEnhanced_fr_CA';

const ActionButton = Action(Button);
const { getLocalisedDataFromResource } = languageLocaleUtils;
const getText = getLocalisedDataFromResource(AdvocacyErrorEnhancedEN, AdvocacyErrorEnhancedFR);
const CTA_MAX_WIDTH = 340;

// Content per enhanced-error variant. `error` (from RwdAdvocacy.ctrlr) selects the heading and body.
// A missing/unknown `error` falls back to the codes-exhausted variant (OPT-23061). Each variant has its
// own heading and invite body copy; both render a Shop Now CTA.
const VARIANTS = {
    // OPT-23096: expired promotion.
    [ERROR.ERR_CAMP_REF_CMP_EXPIRED]: { heading: 'promotionEndedHeading', body: 'promotionEndedBody' },
    // OPT-23061: all promotion codes claimed.
    [ERROR.ERR_CAMP_REF_MAX_COUNT_RCHD]: { heading: 'codesExhaustedHeading', body: 'codesExhaustedBody' },
    // OPT-23304: referee already registered for this promotion.
    [ERROR.ERR_CAMP_REFEREE_ALREADY_REGISTERED]: { heading: 'alreadyRegisteredHeading', body: 'alreadyRegisteredBody' },
    // OPT-23075: referrer redeemed own link.
    [ERROR.ERR_CAMP_REFERRER_SELF_REGISTRATION_NOT_ALLOWED]: { heading: 'selfRegistrationHeading', body: 'selfRegistrationBody' }
};

/**
 * Enhanced Referee error view, gated by the `refereeLandingPageEnhancements` killswitch. Renders a
 * two-column hero + message layout whose heading/body are selected by `error`: `ERR_CAMP_REF_CMP_EXPIRED`
 * shows "This offer has ended." (OPT-23096) and `ERR_CAMP_REF_MAX_COUNT_RCHD` shows the codes-claimed
 * message (OPT-23061). Each variant has its own heading and invite body copy, and both render a Shop Now
 * CTA. A missing or unknown `error` falls back to the codes-claimed variant.
 *
 * @param {Object} props
 * @param {string} [props.error] - Classified app-level error code (`ERROR.*`) selecting the variant; falls back to the codes-claimed variant when missing or unknown.
 * @param {string} [props.apiErrorMessage] - Raw RMS error message; the per-campaign discount (e.g. "20%") is parsed from it for the already-registered heading (OPT-23304).
 * @param {string} props.pageType - Page type forwarded to page-load analytics.
 * @param {Array} [props.globalHeroBanner] - Contentful hero banner; `[0]` renders when it has media.
 * @param {string} [props.globalInvitationText] - "Invited by" caption prefix (paired with referrerFirstName).
 * @param {string} [props.referrerFirstName] - Referrer first name for the caption.
 * @param {Object} [props.referralDisclaimer] - Contentful rich-text disclaimer rendered below the CTA.
 * @returns {JSX.Element} The rendered enhanced error view.
 */
const AdvocacyErrorEnhanced = ({
    error, apiErrorMessage, pageType, globalHeroBanner, globalInvitationText, referrerFirstName, referralDisclaimer
}) => {
    rwdAdvocacyPageBindings.setPageLoadAnaytics(pageType);

    const variant = VARIANTS[error] || VARIANTS[ERROR.ERR_CAMP_REF_MAX_COUNT_RCHD];
    // OPT-23304: the already-registered heading is templated with the per-campaign discount parsed
    // from the RMS message ("...your 20% off!"). Falls back to '20%' when the message omits it.
    // ponytail: '20%' fallback is a hardcode of last resort; the RMS already-registered message
    // always carries the percentage today, so it only guards a malformed/absent message.
    const discount = apiErrorMessage?.match(/\d+\s*%/)?.[0]?.replace(/\s+/g, '') || '20%';
    const heroBanner = globalHeroBanner?.[0];
    const hasBanner = Boolean(heroBanner?.media?.src);

    return (
        <Container
            marginY={[5, 7]}
            paddingX={[4, 5]}
        >
            <Grid
                columns={[1, hasBanner ? 2 : 1]}
                gap={[5, 6]}
                alignItems='start'
            >
                {hasBanner && (
                    <Banner
                        media={heroBanner.media}
                        largeMedia={heroBanner.largeMedia}
                        backgroundColor={heroBanner.backgroundColor}
                    />
                )}
                <Container>
                    {referrerFirstName && globalInvitationText && (
                        <Text
                            is='p'
                            marginBottom={2}
                            data-at={Sephora.debug.dataAt('referrer_text')}
                            children={`${globalInvitationText} ${referrerFirstName}`}
                        />
                    )}
                    <Text
                        is='h1'
                        fontWeight='bold'
                        fontSize={['lg', 'xl']}
                        children={getText(variant.heading, [discount])}
                    />
                    {variant.body && (
                        <Text
                            is='p'
                            marginTop={3}
                            children={getText(variant.body)}
                        />
                    )}
                    <ActionButton
                        variant='primary'
                        children={getText('shopNow')}
                        width='100%'
                        marginTop={5}
                        maxWidth={['100%', CTA_MAX_WIDTH]}
                        data-at={Sephora.debug.dataAt('shop_now_btn')}
                        action={{ type: 'ActionLinkInternal', targetUrl: '/' }}
                    />
                    {referralDisclaimer && (
                        <>
                            <Divider marginY={4} />
                            <Box
                                fontSize='sm'
                                color='gray'
                            >
                                <Copy
                                    content={referralDisclaimer}
                                    data-at={Sephora.debug.dataAt('offer_disclaimer')}
                                    marginTop={0}
                                    marginBottom={0}
                                />
                            </Box>
                        </>
                    )}
                </Container>
            </Grid>
        </Container>
    );
};

export default wrapFunctionalComponent(AdvocacyErrorEnhanced, 'AdvocacyErrorEnhanced');
